/**
 * 字幕解析器
 * 支持 SRT 和 VTT 格式
 *
 * SRT 格式：
 * 1
 * 00:00:01,000 --> 00:00:04,000
 * Hello, world!
 *
 * VTT 格式：
 * WEBVTT
 *
 * 00:00:01.000 --> 00:00:04.000
 * Hello, world!
 */

import { SubtitleSegment } from '../../db/shadowDb';

/** 时间字符串转毫秒 */
function parseTimestamp(timestamp: string): number {
  // 支持 HH:MM:SS,mmm (SRT) 和 HH:MM:SS.mmm (VTT)
  const normalized = timestamp.trim().replace(',', '.');
  const parts = normalized.split(':');

  let hours = 0;
  let minutes = 0;
  let secondsWithMs = '0';

  if (parts.length === 3) {
    // HH:MM:SS.mmm
    hours = parseInt(parts[0], 10);
    minutes = parseInt(parts[1], 10);
    secondsWithMs = parts[2];
  } else if (parts.length === 2) {
    // MM:SS.mmm
    minutes = parseInt(parts[0], 10);
    secondsWithMs = parts[1];
  }

  const [seconds, ms = '0'] = secondsWithMs.split('.');
  const milliseconds = parseInt(ms.padEnd(3, '0').slice(0, 3), 10);

  return (hours * 3600 + minutes * 60 + parseInt(seconds, 10)) * 1000 + milliseconds;
}

/** 解析时间范围行 */
function parseTimeLine(line: string): { startMs: number; endMs: number } | null {
  // 匹配 "00:00:01,000 --> 00:00:04,000" 或 "00:00:01.000 --> 00:00:04.000"
  const match = line.match(/(\d{1,2}:\d{2}:\d{2}[.,]\d{3})\s*-->\s*(\d{1,2}:\d{2}:\d{2}[.,]\d{3})/);
  if (!match) return null;

  return {
    startMs: parseTimestamp(match[1]),
    endMs: parseTimestamp(match[2]),
  };
}

/** 检测字幕格式 */
export function detectSubtitleFormat(content: string): 'srt' | 'vtt' | 'unknown' {
  const trimmed = content.trim();
  if (trimmed.startsWith('WEBVTT')) {
    return 'vtt';
  }
  // SRT 通常以数字序号开始
  if (/^\d+\s*\n\d{1,2}:\d{2}:\d{2}/.test(trimmed)) {
    return 'srt';
  }
  // 尝试匹配时间行
  if (/\d{1,2}:\d{2}:\d{2}[.,]\d{3}\s*-->\s*\d{1,2}:\d{2}:\d{2}[.,]\d{3}/.test(trimmed)) {
    return 'srt';
  }
  return 'unknown';
}

/** 解析 SRT 字幕 */
export function parseSrt(content: string): SubtitleSegment[] {
  const segments: SubtitleSegment[] = [];
  const blocks = content.trim().split(/\n\s*\n/);

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    if (lines.length < 2) continue;

    // 查找时间行（可能有序号行在前面）
    let timeLineIndex = 0;
    if (/^\d+$/.test(lines[0].trim())) {
      timeLineIndex = 1;
    }

    const timeLine = lines[timeLineIndex];
    const timing = parseTimeLine(timeLine);
    if (!timing) continue;

    // 剩余行是字幕文本
    const textLines = lines.slice(timeLineIndex + 1);
    const text = textLines.join(' ').trim();
    if (!text) continue;

    segments.push({
      index: segments.length,
      startMs: timing.startMs,
      endMs: timing.endMs,
      text,
    });
  }

  return segments;
}

/** 解析 VTT 字幕 */
export function parseVtt(content: string): SubtitleSegment[] {
  const segments: SubtitleSegment[] = [];
  const lines = content.trim().split('\n');

  // 跳过 WEBVTT 头部
  let i = 0;
  while (i < lines.length && !lines[i].includes('-->')) {
    i++;
  }

  let currentTiming: { startMs: number; endMs: number } | null = null;
  let currentText: string[] = [];

  for (; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line === '') {
      // 空行表示一个 cue 结束
      if (currentTiming && currentText.length > 0) {
        segments.push({
          index: segments.length,
          startMs: currentTiming.startMs,
          endMs: currentTiming.endMs,
          text: currentText.join(' ').trim(),
        });
      }
      currentTiming = null;
      currentText = [];
      continue;
    }

    const timing = parseTimeLine(line);
    if (timing) {
      // 如果之前有未保存的 cue，先保存
      if (currentTiming && currentText.length > 0) {
        segments.push({
          index: segments.length,
          startMs: currentTiming.startMs,
          endMs: currentTiming.endMs,
          text: currentText.join(' ').trim(),
        });
      }
      currentTiming = timing;
      currentText = [];
      continue;
    }

    // 跳过 cue 标识符行（VTT 可选的 cue id）
    if (!currentTiming && !line.includes('-->')) {
      continue;
    }

    // 文本行
    if (currentTiming) {
      // 移除 VTT 格式标签如 <c> 等
      const cleanedLine = line.replace(/<[^>]+>/g, '');
      if (cleanedLine) {
        currentText.push(cleanedLine);
      }
    }
  }

  // 处理最后一个 cue
  if (currentTiming && currentText.length > 0) {
    segments.push({
      index: segments.length,
      startMs: currentTiming.startMs,
      endMs: currentTiming.endMs,
      text: currentText.join(' ').trim(),
    });
  }

  return segments;
}

/** 解析字幕文件（自动检测格式） */
export function parseSubtitle(content: string): SubtitleSegment[] {
  const format = detectSubtitleFormat(content);

  if (format === 'vtt') {
    return parseVtt(content);
  }

  // 默认按 SRT 解析
  return parseSrt(content);
}

/** 从文件读取字幕 */
export async function parseSubtitleFile(file: File): Promise<SubtitleSegment[]> {
  const content = await file.text();
  return parseSubtitle(content);
}

/** 格式化毫秒为时间字符串 (MM:SS) */
export function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** 格式化毫秒为完整时间字符串 (HH:MM:SS) */
export function formatFullTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
